import CircularProgress from '@material-ui/core/CircularProgress';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles } from '@material-ui/styles';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from '../../helpers/propTypes';
import { userMetrics } from '../../userMetrics';
import ButtonWithArrow from '../Buttons/ButtonWithArrow';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    backgroundColor: 'white',
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.15)',
    zIndex: 100,
    display: 'flex',
    flex: '0 0 auto',

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      justifyContent: 'space-around',
    },

    '@media print': {
      display: 'none',
    },
  },

  children: {
    flexGrow: 1,
    padding: '7px 16px',
    display: 'flex',
    alignItems: 'center',
  },

  buttons: {
    display: 'flex',
    flexGrow: 1,

    '& > *': {
      padding: '7px 16px',
      display: 'flex',
      alignItems: 'center',
    },
  },

  spacer: {
    flexGrow: 1,
    padding: 0,
  },

  backButton: {
    color: theme.palette.grey[600],

    '& > *': {
      minWidth: 0,
    },

    [theme.breakpoints.down('xs')]: {
      minWidth: '48px',
      paddingRight: 0,
    },
  },

  warningButton: {
    paddingRight: 0,
    color: theme.palette.warning.main,
  },

  continueButton: {
    minWidth: 200,

    '& > *': {
      width: '100%',
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
}));

function Footer({
  children,
  formIsComplete,
  onBackClick,
  onContinueClick,
  footerText,
  loading,
  liveRegionId,
  warning,
  nextPageName,
  prevPageName,
}) {
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.down('xs'));
  const classes = useStyles();
  const isSubmitPage = nextPageName === 'completed';
  const nextBtnText =
    footerText ||
    (isSubmitPage ? (
      <FormattedMessage defaultMessage="Submit" id="Footer.submit" />
    ) : (
      <FormattedMessage defaultMessage="Next" id="Footer.next" />
    ));

  return (
    <footer className={classes.root}>
      {children && <div className={classes.children}>{children}</div>}
      <div className={classes.buttons}>
        {onBackClick && prevPageName && prevPageName !== 'questionnaire' && (
          <div className={classes.backButton}>
            <ButtonWithArrow
              data-testid="back-button"
              aria-controls={liveRegionId}
              arrowSide="left"
              compact
              hideText={small}
              onClick={onBackClick}
            >
              <FormattedMessage defaultMessage="Back" id="Footer.back" />
            </ButtonWithArrow>
          </div>
        )}
        <span className={classes.spacer} />
        {warning && <div className={classes.warningButton}>{warning}</div>}
        <div className={classes.continueButton}>
          <ButtonWithArrow
            data-testid="continue-button"
            aria-controls={liveRegionId}
            color="primary"
            compact
            disabled={!formIsComplete || loading}
            onClick={() => {
              if (isSubmitPage && formIsComplete) {
                userMetrics.track('Submit button pressed');
              }
              onContinueClick();
            }}
            variant="contained"
          >
            {loading ? <CircularProgress size={28} /> : nextBtnText}
          </ButtonWithArrow>
        </div>
      </div>
    </footer>
  );
}

Footer.propTypes = {
  children: PropTypes.node,
  footerText: PropTypes.node.isRequired,
  formIsComplete: PropTypes.bool.isRequired,
  liveRegionId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  nextPageName: PropTypes.string,
  onBackClick: PropTypes.func,
  onContinueClick: PropTypes.func.isRequired,
  prevPageName: PropTypes.string,
  warning: PropTypes.node,
};

Footer.defaultProps = {
  children: undefined,
  loading: false,
  nextPageName: undefined,
  onBackClick: undefined,
  prevPageName: undefined,
  warning: undefined,
};

export default Footer;
